// @ts-strict-ignore
import { BaseReplaceArticleManager } from './base-replace-article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArrayUtils } from '@shared/util/array-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ReplaceLabelingArticleHelper } from '@project/components/label/replace-labeling-helper/replace-labeling-article.helper';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { OpenLabelEditorHelper } from '@project/components/label/label-editor/service/open-label-editor.helper';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { AlternativeUsageConnectionData } from '../model/alternative-usage-connection-data';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { DirectPrintingHelper } from '@article/helper/direct-printing/direct-printing.helper';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { ProjectHelper } from '@project/helper/project.helper';
import { ProjectModel } from '@project/model/project.model';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { SelectionModel } from '../model/selection.model';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { LabelingArticleRotationHelper } from '../../label/helper/labeling-article-rotation.helper';
import { RotateArticlePresenter } from '../presenter/rotate-article.presenter';
import { Observable, Subject } from 'rxjs';
import { Log } from '@shared/debug/log';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

interface RangeOnLineReplaceInfo {
  selectedArticlesOnRail: ArticleContainer[];
}

@Injectable({ providedIn: 'root' })
export class DefaultReplaceArticleManager extends BaseReplaceArticleManager {
  private static readonly REPLACE_ARTICLE_MESSAGE_TYPE: string = 'replaceArticleOnMountingRail';
  private static readonly REPLACE_ACCESSORIES_MESSAGE_TYPE: string = 'replaceAccessories';

  private _rangeOnLineDatas: RangeOnLineReplaceInfo[] = [];
  private _articleUsageIdsToReplace: string[] = [];
  private _allAccessoriesReconnected: boolean = true;
  private _detachLabelStripOperation: UndoDetachArticlesOperation = null;

  private _replacedArticles$ = new Subject<boolean>();

  constructor(
    userModel: UserModel,
    containerService: ContainerService,
    selectionModel: SelectionModel,
    selectionModelService: SelectionModelService,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    projectHelper: ProjectHelper,
    undoRedoHelper: UndoRedoHelper,
    articleAccessoryManager: ArticleAccessoryManager,
    directPrintingHelper: DirectPrintingHelper,
    undoRedoUtils: UndoRedoUtils,
    labelingArticleRotationHelper: LabelingArticleRotationHelper,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    rotateArticlePresenter: RotateArticlePresenter,
    labelingUtils: LabelingUtils,
    articleContainerUtils: ArticleContainerUtils,
    replaceLabelingUtils: ReplaceLabelingUtils,
    insertArticleUtils: InsertArticleUtils,
    insertArticleService: InsertArticleService,
    detachArticleUsageService: DetachArticleUsageService,
    articleConnectionService: ArticleConnectionService,
    private alertHelper: AlertHelper,
    private mountingRailHelper: MountingRailHelper,
    private openLabelEditorHelper: OpenLabelEditorHelper,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private replaceBusbarService: ReplaceBusbarService
  ) {
    super(
      userModel,
      selectionModel,
      selectionModelService,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      projectHelper,
      undoRedoHelper,
      articleAccessoryManager,
      directPrintingHelper,
      containerService,
      undoRedoUtils,
      insertArticleUtils,
      labelingArticleRotationHelper,
      replaceLabelingUtils,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      rotateArticlePresenter,
      labelingUtils,
      articleContainerUtils,
      insertArticleService,
      detachArticleUsageService,
      articleConnectionService
    );
  }

  public get replacedArticles$(): Observable<boolean> {
    return this._replacedArticles$.asObservable()
  }

  protected async init(articleContainersOnRail: ArticleContainer[], childContainers: ArticleContainer[], article: ArticleVO,
    unremovedComplexPartArticles: ArticleContainer[]): Promise<void> {
    await super.init(articleContainersOnRail, childContainers, article, unremovedComplexPartArticles);

    this._rangeOnLineDatas = this.mountingRailHelper.createRangeOnLineArticleDataByArticles(articleContainersOnRail);

    const labelStripContainers: ArticleContainer[] = this.replaceLabelingUtils
      .getLabelStripsToRecreateByArticles(articleContainersOnRail)
      .filter((container) => !ArticleUsageUtils.isCompositePartArticleUsage(container.articleUsage));
    const busbarContainers: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByArticles(articleContainersOnRail)
      .filter(container => !ArticleUsageUtils.isCompositePartArticleUsage(container.articleUsage));
    await this.replaceBusbarHelper.detachBusbarsContainersUnchecked(busbarContainers);

    this.removeContainersFromArticlesToDetach(labelStripContainers);
    this._detachLabelStripOperation = await this.replaceLabelingHelper.detachLabelStrips(labelStripContainers);
  }

  protected async startProcessingAccessoryReplacement(accessoryContainers: ArticleContainer[]): Promise<void> {
    this._rangeOnLineDatas = [
      { selectedArticlesOnRail: accessoryContainers }
    ];
    await this.replaceByNextRangeOnLineData();
  }

  protected async startProcessingPreparedArticles(): Promise<void> {
    await this.replaceByNextRangeOnLineData();
  }

  private async replaceByNextRangeOnLineData(): Promise<void> {
    if (ArrayUtils.isNotEmpty(this._rangeOnLineDatas)) {
      const processedArticleData: RangeOnLineReplaceInfo = this._rangeOnLineDatas[0];
      this._rangeOnLineDatas.splice(this._rangeOnLineDatas.indexOf(processedArticleData), 1);
      this._articleUsageIdsToReplace = ArticleContainerUtils.extractArticleGUIDs(processedArticleData.selectedArticlesOnRail);
      await this.replaceNextArticle();
    } else {
      await this.completeReplaceOperation();
    }
  }

  protected async replaceNextArticle(): Promise<void> {
    if (this._articleUsageIdsToReplace.length > 0) {
      this.processedUsageId = this._articleUsageIdsToReplace[0];
      this._articleUsageIdsToReplace.splice(this._articleUsageIdsToReplace.indexOf(this.processedUsageId), 1);

      if (this._accessoryReplaceEnabled) {
        try {
          await this.replaceAccessoryArticle(DefaultReplaceArticleManager.REPLACE_ACCESSORIES_MESSAGE_TYPE);
          await this.replaceAccessories_success();
        } catch (error) {
          Log.error('Could not replace accessory article: ', error);
          await this.replaceAccessories_failure();
        }
      } else {
        try {
          const data: InsertedArticleData = await this.replaceArticle(this.processedUsageId, DefaultReplaceArticleManager.REPLACE_ARTICLE_MESSAGE_TYPE, false);
          await this.articleOnMountingRailInserted(data);
        } catch (error) {
          Log.error('Could not insert article', error);
          await this.replaceArticleOnMountingRail_failure();
        }
      }
    } else {
      await this.replaceByNextRangeOnLineData();
    }
  }

  private async replaceAccessories_success(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    const createdContainers: ArticleContainer[] = this._creationManager.getCreatedArticleContainers();
    if (ArrayUtils.isEmpty(createdContainers)) {
      await this.replaceNextArticle();
      return;
    }

    const alreadyDetachedIds: string[] = !this.detachArticlesOperation ? [] : this.detachArticlesOperation.extractArticleUsageIds();
    const detachContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(
      this._creationManager.detachArticleUsageIds
    );
    const accessoryDetachOperation: UndoDetachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByArticleContainers(
      detachContainers.filter((container) => alreadyDetachedIds.indexOf(container.articleGUID) < 0)
    );
    this.mergeDetachOperation(accessoryDetachOperation);

    if (detachContainers.length > 0) {
      await this.detachArticleContainers(detachContainers);
    }

    createdContainers.map((c) => c.articleGUID).forEach((guid) => this._articleIdToReplacedArticleId.setValue(guid, this.processedUsageId));
    this.storeReplacedTextureAreasInfo([this.containerService.findArticleContainerInGlobal(this.processedUsageId)]);

    const replacedContainerIds = await this.postProcessCreatedContainersByBasePart(
      createdContainers,
      this.containerService.findArticleContainerInGlobal(this.processedUsageId)
    );
    ArrayUtils.mergeArrays(replacedContainerIds, ArticleContainerUtils.extractArticleGUIDs(detachContainers));
    this.replaceLabelingHelper.replaceArticleContainers(createdContainers, replacedContainerIds);
    // for accessories, the move map will always be empty
    await this.processingCreatedArticles(ArticleContainerUtils.extractArticleGUIDs(createdContainers), new Dictionary<string, Vector3>());
  }

  private async articleOnMountingRailInserted(data: any): Promise<void> {
    if (!this.isActive) {
      return;
    }

    data.insertedArticleGUIDs.forEach(guid => this._articleIdToReplacedArticleId.setValue(guid, this.processedUsageId));
    this.storeReplacedTextureAreasInfo([this.containerService.findArticleContainerInGlobal(this.processedUsageId)]);
    const replacedContainerIds = await this.postProcessCreatedContainersByBasePart(
      this.containerService.findArticleContainersInGlobalByGuids(data.insertedArticleGUIDs),
      this.containerService.findArticleContainerInGlobal(this.processedUsageId));
    this.replaceLabelingHelper.replaceArticleContainers(
      this.containerService.findArticleContainersInGlobalByGuids(data.insertedArticleGUIDs),
      replacedContainerIds
    );
    await this.processingCreatedArticles(data.insertedArticleGUIDs, data.moveArticlesMap);
  }

  private async postProcessCreatedContainersByBasePart(createdContainers: ArticleContainer[], baseReplacedContainer: ArticleContainer): Promise<string[]> {
    if (!baseReplacedContainer) {
      return;
    }

    const acceptingConnections: Connection[] = ConnectionInfoUtils.getFilteredAcceptingCubesByArticleContainers(createdContainers);
    const baseContainers = this.getBaseContainers(baseReplacedContainer);

    const allReplacedContainers: ArticleContainer[] = [];
    for (const baseContainer of baseContainers) {
      const replacedContainers: ArticleContainer[] = await this.postProcessCreatedContainersByReplacedContainer(acceptingConnections, baseContainer);
      ArrayUtils.mergeArrays(allReplacedContainers, replacedContainers);
    }

    await this.detachArticleContainers(allReplacedContainers);

    return ArticleContainerUtils.extractArticleGUIDs(allReplacedContainers);
  }

  private async postProcessCreatedContainersByReplacedContainer(acceptingConnections: Connection[], replacedContainer: ArticleContainer): Promise<ArticleContainer[]> {
    let alternativeUsageConnectionDatas: AlternativeUsageConnectionData[] = [];
    let notReconnectedAccessories: ArticleContainer[] = [];

    const accessoriesToReconnect: ArticleContainer[] = this.getAccessoriesToReconnect(replacedContainer);
    for (const accessoryContainer of accessoriesToReconnect) {
      const accessoryAlternativeConnectionDatas: AlternativeUsageConnectionData[] = await this.reconnectAccessoryArticle(accessoryContainer, replacedContainer, acceptingConnections);
      if (accessoryAlternativeConnectionDatas.length > 0) {
        this.removeContainersFromArticlesToDetach([accessoryContainer]);
        const childOfAccessoryArticlesToReconnect: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers([accessoryContainer]);
        this.removeContainersFromArticlesToDetach(childOfAccessoryArticlesToReconnect);
        ArticleUsageUtils.removeAssociationsWithUsage(replacedContainer.articleUsage, accessoryContainer.articleUsage.id);
        ArticleUsageUtils.removeAssociationsWithUsage(accessoryContainer.articleUsage, replacedContainer.articleGUID);
        alternativeUsageConnectionDatas = alternativeUsageConnectionDatas.concat(accessoryAlternativeConnectionDatas);
      } else {
        notReconnectedAccessories.push(accessoryContainer);
      }
    }

    this._articleIdToAlternativeUsageConnectionDatas.setValue(replacedContainer.articleGUID, alternativeUsageConnectionDatas);

    if (notReconnectedAccessories.length > 0) {
      this._allAccessoriesReconnected = false;
      const childOfAccessoryArticlesToDelete: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers(notReconnectedAccessories);
      notReconnectedAccessories = notReconnectedAccessories.concat(childOfAccessoryArticlesToDelete);
    }

    if (this.commonMoveArticlesMap && this.commonMoveArticlesMap.containsKey(replacedContainer.articleGUID)) {
      this.commonMoveArticlesMap.remove(replacedContainer.articleGUID);
    }

    return [replacedContainer, ...notReconnectedAccessories];
  }

  private getBaseContainers(baseContainerPart: ArticleContainer): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    result.push(baseContainerPart);

    if (this.compositeArticleInfoUtils.isCompositePartArticle(baseContainerPart)) {
      const compositeArticleUsageId = CompositeArticleInfoUtils.getCompositeArticleUsageId(baseContainerPart);
      const allConnectedContainers = this.articleContainerUtils.getConnectedArticlesRecursively(baseContainerPart);
      result.push(
        ...allConnectedContainers.filter((container) => compositeArticleUsageId === CompositeArticleInfoUtils.getCompositeArticleUsageId(container))
      );
    }
    return result;
  }

  private getAccessoriesToReconnect(container: ArticleContainer): ArticleContainer[] {
    let connectedContainers = this.containerService.getConnectedArticleContainers(container.articleUsage);
    if (this.compositeArticleInfoUtils.isCompositePartArticle(container)) {
      const compositeArticleUsageId = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
      connectedContainers = connectedContainers.filter(
        (connectedContainer) => compositeArticleUsageId !== CompositeArticleInfoUtils.getCompositeArticleUsageId(connectedContainer)
      );
    }
    return connectedContainers;
  }

  private async replaceAccessories_failure(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    await this.revertDynamicArticlesCreation();

    this.clear();
    this.selectionModelService.selectArticlesByUsageIds(this.selectedArticleIds, true);
    this._replacedArticles$.next(false);
  }

  private async replaceArticleOnMountingRail_failure(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    await this.finalizeFailureProcessingArticle();
  }

  protected async finalizeFailureProcessingArticle(): Promise<void> {
    await super.finalizeFailureProcessingArticle();

    const currentContainer = this.containerService.findArticleContainerInGlobal(this.processedUsageId);
    if (currentContainer) {
      this.removeArticlesFromDetachOperationIfNoParentDetached(this.containerService.getConnectedArticleContainers(currentContainer.articleUsage));
    }

    await this.replaceNextWithDelay();
  }

  private removeArticlesFromDetachOperationIfNoParentDetached(containers: ArticleContainer[]): void {
    if (!this.detachArticlesOperation) {
      return;
    }

    const idsToDetach: string[] = this.detachArticlesOperation.extractArticleUsageIds();
    const childContainersToCheck: ArticleContainer[] = [];
    for (const container of containers) {
      const childContainerIndex: number = idsToDetach.indexOf(container.articleGUID);
      if (childContainerIndex >= 0) {
        const backwardIds = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(container.articleUsage);
        if (!ArrayUtils.sourceArrayContainsElementFromTargetArray(idsToDetach, backwardIds)) {
          this.revertArticleDetachOperation(container.articleGUID);
          idsToDetach.splice(childContainerIndex, 1);
          ArrayUtils.mergeArrays(childContainersToCheck, this.containerService.getConnectedArticleContainers(container.articleUsage));
        }
      }
    }

    if (childContainersToCheck.length > 0) {
      this.removeArticlesFromDetachOperationIfNoParentDetached(childContainersToCheck);
    }
  }

  private async completeReplaceOperation(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    if (this.replaceLabelingHelper.pendingLabelStrips.some((strip) => !strip.isConnectable())) {
      this._allAccessoriesReconnected = false;
    }

    await this.detachArticleContainers(this.containerService.findArticleContainersInGlobalByGuids(this.detachArticlesOperation.extractArticleUsageIds()));

    let succeed: boolean = true;
    if (ArrayUtils.isNotEmpty(this.insertedArticleUsageIds)) {
      await this.pushUndoAlternativeReplaceOperation(this.getAdditionalUndoOperations());
      this.selectionModelService.selectArticlesByUsageIds(this.selectedArticleIds, true);

      const insertedContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(this.insertedArticleUsageIds);
      this.openLabelEditorHelper.showLabelEditorForCoverLabelArticles(insertedContainers);

      if (!this._allAccessoriesReconnected) {
        this.alertHelper.showError(
          'defaultReplaceArticleManager.title.accessories_not_reconnected',
          'defaultReplaceArticleManager.message.accessories_not_reconnected'
        );
      }
    } else {
      succeed = false;

      if (this.detachArticlesOperation && this.detachArticlesOperation.extractArticleUsageIds().length > 0) {
        await this.detachArticlesOperation.performUndo();
      }
      await this.revertDynamicArticlesCreation();
    }

    this.clear();
    this._replacedArticles$.next(succeed);
  }

  private async revertDynamicArticlesCreation(): Promise<void> {
    if (this._detachLabelStripOperation && this._detachLabelStripOperation.extractArticleUsageIds().length > 0) {
      this.replaceLabelingHelper.clear();
      await this._detachLabelStripOperation.performUndo();
    }
    if (this.replaceBusbarHelper.pendingBusbars.length > 0) {
      for (const busbar of this.replaceBusbarHelper.pendingBusbars) {
        busbar.detachOperation.usageIdsToConnect = busbar.connectedUsageIds;
        busbar.detachOperation.performUndo();
      }
      this.replaceBusbarHelper.clear();
    }
  }

  protected async pushUndoAlternativeReplaceOperation(additionalUndoOperations: IOperation[] = null): Promise<void> {
    this.mergeDetachOperation(this._detachLabelStripOperation);
    await super.pushUndoAlternativeReplaceOperation(additionalUndoOperations);
  }

  private mergeDetachOperation(additionalDetachOperation: UndoDetachArticlesOperation): void {
    if (additionalDetachOperation) {
      if (this.detachArticlesOperation) {
        this.detachArticlesOperation.merge(additionalDetachOperation);
      } else {
        this.detachArticlesOperation = additionalDetachOperation;
      }
    }
  }

  protected clear(): void {
    super.clear();

    this._rangeOnLineDatas = [];
    this._articleUsageIdsToReplace = [];
    this._allAccessoriesReconnected = true;
    this._detachLabelStripOperation = null;
  }
}
